// Reusable helper: fetch PoW challenge, solve off-thread, submit verification.
// Every UI flow that sends email verification should call sendVerificationWithPoW.

import { normalizeEmail } from './sha256';
import { solveChallenge } from './powSolver';

export { normalizeEmail };

export async function sendVerificationWithPoW(email, honeypotValue, options) {
  var onStatusChange = options && options.onStatusChange;
  var normalized = normalizeEmail(email);

  var attempt = async function() {
    if (onStatusChange) onStatusChange('Securing verification request\u2026');

    // 1. Fetch challenge
    var challengeResp = await fetch(
      '/mlapi/auth/email-verification-challenge?email=' +
        encodeURIComponent(normalized)
    );
    if (!challengeResp.ok) {
      throw new Error('Failed to initiate verification');
    }
    var challenge = await challengeResp.json();

    // 2. Worker timeout: challenge expiry minus 5 s margin, clamped to [1 s, 60 s]
    var timeoutMs = Math.min(
      Math.max((challenge.expiresInSeconds - 5) * 1000, 1000),
      60000
    );

    // 3. Solve PoW off main thread
    var solveStart = Date.now();
    var result = await solveChallenge({
      nonce: challenge.nonce,
      normalizedEmail: normalized,
      difficultyBits: challenge.difficultyBits,
      timeoutMs: timeoutMs,
    });

    // 4. Respect minSolveMilliseconds (delay without blocking UI)
    if (challenge.minSolveMilliseconds) {
      var elapsed = Date.now() - solveStart;
      if (elapsed < challenge.minSolveMilliseconds) {
        await new Promise(function(r) {
          setTimeout(r, challenge.minSolveMilliseconds - elapsed);
        });
      }
    }

    // 5. Submit verification request
    var resp = await fetch('/mlapi/auth/send-email-verification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: normalized,
        challengeId: challenge.challengeId,
        challengeCounter: result.counter,
        companyWebsite: honeypotValue || '',
      }),
    });

    return resp;
  };

  // Try up to two attempts; on expired/invalid challenge retry once at most.
  for (var tries = 0; tries < 2; tries++) {
    try {
      var resp = await attempt();
      // Server may reject with 400/409/410 if challenge expired/invalid
      if (
        (resp.status === 400 || resp.status === 409 || resp.status === 410) &&
        tries === 0
      ) {
        continue;
      }
      return resp;
    } catch {
      if (tries === 0) continue;
    }
  }
  throw new Error('Unable to verify at this time. Please try again.');
}
